/**
 * ApplyDiffModal — section-by-section diff between the veteran's structured
 * answers and the AI-rewritten output, with per-section opt-in checkboxes.
 *
 * Why a per-section modal (not a per-prompt diff):
 *   - The 5 rewrite actions return a coherent 7-section document, not
 *     prompt-aligned chunks. Aligning back to prompt IDs would lose the
 *     AI's narrative flow.
 *   - Per-section overrides live in the answers map under `__ai_override_<sid>`
 *     and are picked up by the SectionedPreview without mutating the
 *     structured answers. The vet can revert at any time.
 *
 * Apple Sequoia visual treatment: frosted overlay, 12px radii, two-column
 * "Current vs Proposed" with the proposed column highlighted in the brand
 * accent so the eye lands on what would change.
 */
import React, { useMemo, useState } from "react";
import { X, CheckCircle, Sparkle, ArrowRight, Circle } from "@phosphor-icons/react";
import { buildSectionDiff, applyOverrides } from "@/lib/aiDraftMerge";

export default function ApplyDiffModal({
  prompts, answers, parsedSections, onCancel, onApply,
}) {
  const diffRows = useMemo(
    () => buildSectionDiff(prompts || [], answers || {}, parsedSections || {}),
    [prompts, answers, parsedSections],
  );

  // Default selection: every section that the AI actually produced. The user
  // can uncheck individual sections to keep their structured text intact.
  const [selected, setSelected] = useState(() => {
    const init = {};
    diffRows.forEach((r) => { init[r.sectionId] = r.defaultSelected; });
    return init;
  });

  function toggle(sid) {
    setSelected((s) => ({ ...s, [sid]: !s[sid] }));
  }
  function selectAll(value) {
    const next = {};
    diffRows.forEach((r) => { next[r.sectionId] = value; });
    setSelected(next);
  }
  function handleApply() {
    const chosen = diffRows.filter((r) => selected[r.sectionId]).map((r) => r.sectionId);
    const nextAnswers = applyOverrides(answers || {}, parsedSections || {}, chosen);
    onApply(nextAnswers, chosen);
  }

  const selectedCount = diffRows.filter((r) => selected[r.sectionId]).length;

  return (
    <div
      data-testid="apply-diff-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Apply AI draft to your statement"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1200,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(15,23,42,0.45)", backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        padding: 24,
      }}
    >
      <div
        style={{
          background: "var(--surface, #ffffff)",
          borderRadius: 16,
          boxShadow: "0 24px 60px rgba(15,23,42,0.30)",
          width: "min(1100px, 96vw)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "14px 18px",
            borderBottom: "1px solid var(--border)",
            background: "linear-gradient(135deg, rgba(31,58,107,0.06), rgba(31,58,107,0.02))",
          }}
        >
          <Sparkle size={18} weight="duotone" color="#1F3A6B" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.005em" }}>
              Apply AI draft to your statement
            </div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
              Review section-by-section. Only checked sections are applied. Your structured answers stay intact —
              you can revert any time with the Revert button on the toolbar.
            </div>
          </div>
          <button
            type="button"
            data-testid="apply-diff-modal-close"
            onClick={onCancel}
            aria-label="Close"
            style={{
              border: "none", background: "transparent", cursor: "pointer",
              padding: 6, borderRadius: 8,
            }}
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Toolbar */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 18px",
            borderBottom: "1px solid var(--border)",
            background: "var(--surface-2, #F8FAFC)",
          }}
        >
          <span className="mono" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-secondary)" }}>
            {selectedCount} OF {diffRows.length} SECTION{diffRows.length === 1 ? "" : "S"} SELECTED
          </span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            data-testid="apply-diff-select-all"
            onClick={() => selectAll(true)}
          >
            Select all
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            data-testid="apply-diff-deselect-all"
            onClick={() => selectAll(false)}
          >
            Deselect all
          </button>
        </div>

        {/* Diff body */}
        <div style={{ overflowY: "auto", padding: 18, display: "grid", gap: 14, flex: 1 }}>
          {diffRows.length === 0 && (
            <div data-testid="apply-diff-empty" style={{ padding: 32, textAlign: "center", color: "var(--text-secondary)" }}>
              The AI output didn&apos;t include any recognisable section headers. Use Copy instead to paste it manually.
            </div>
          )}
          {diffRows.map((row) => (
            <DiffRow
              key={row.sectionId}
              row={row}
              checked={!!selected[row.sectionId]}
              onToggle={() => toggle(row.sectionId)}
            />
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end",
            padding: "12px 18px",
            borderTop: "1px solid var(--border)",
            background: "var(--surface-2, #F8FAFC)",
          }}
        >
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            data-testid="apply-diff-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--accent btn--sm"
            data-testid="apply-diff-apply"
            disabled={selectedCount === 0}
            onClick={handleApply}
          >
            <ArrowRight size={12} weight="bold" /> APPLY {selectedCount} SECTION{selectedCount === 1 ? "" : "S"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DiffRow — one section with current/proposed side-by-side + checkbox
// ---------------------------------------------------------------------------

function DiffRow({ row, checked, onToggle }) {
  const empty = !row.currentText.trim();
  return (
    <div
      data-testid={`apply-diff-row-${row.sectionId}`}
      style={{
        border: `1px solid ${checked ? "rgba(31,58,107,0.40)" : "var(--border)"}`,
        borderRadius: 12,
        background: checked ? "rgba(31,58,107,0.03)" : "var(--surface)",
        padding: 14,
        transition: "background 160ms ease, border-color 160ms ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <button
          type="button"
          onClick={onToggle}
          data-testid={`apply-diff-toggle-${row.sectionId}`}
          aria-label={`${checked ? "Deselect" : "Select"} ${row.title}`}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 22, height: 22, borderRadius: 6,
            border: `1.5px solid ${checked ? "#1F3A6B" : "var(--border)"}`,
            background: checked ? "#1F3A6B" : "var(--surface)",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {checked ? <CheckCircle size={14} weight="fill" color="#fff" /> : <Circle size={12} weight="bold" color="var(--text-secondary)" />}
        </button>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.05em", color: "var(--text-primary)", textTransform: "uppercase" }}>
          {row.title}
        </div>
        {empty && (
          <span
            className="mono"
            data-testid={`apply-diff-empty-badge-${row.sectionId}`}
            style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em",
              padding: "2px 8px",
              borderRadius: 999,
              background: "rgba(16,185,129,0.10)",
              border: "1px solid rgba(16,185,129,0.30)",
              color: "#047857",
            }}
          >
            NEW CONTENT
          </span>
        )}
        {!empty && row.hasChange && (
          <span
            className="mono"
            data-testid={`apply-diff-changed-badge-${row.sectionId}`}
            style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em",
              padding: "2px 8px",
              borderRadius: 999,
              background: "rgba(217,119,6,0.10)",
              border: "1px solid rgba(217,119,6,0.30)",
              color: "#b45309",
            }}
          >
            REPLACES CURRENT
          </span>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {/* Current */}
        <div
          data-testid={`apply-diff-current-${row.sectionId}`}
          style={{
            padding: 12,
            borderRadius: 10,
            background: "var(--surface-2, #F1F5F9)",
            border: "1px solid var(--border)",
            minHeight: 80,
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)", marginBottom: 6 }}>
            CURRENT
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>
            {row.currentText.trim() || <span style={{ color: "var(--text-secondary)", fontStyle: "italic" }}>(empty)</span>}
          </div>
        </div>

        {/* Proposed */}
        <div
          data-testid={`apply-diff-proposed-${row.sectionId}`}
          style={{
            padding: 12,
            borderRadius: 10,
            background: "rgba(31,58,107,0.05)",
            border: "1px solid rgba(31,58,107,0.25)",
            minHeight: 80,
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "#1F3A6B", marginBottom: 6 }}>
            AI PROPOSED
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>
            {row.proposedText}
          </div>
        </div>
      </div>
    </div>
  );
}
